import {
  AggregatedOrderedProject,
  useUpdateOrderedProject,
} from '~/common/api/orderingServiceApi/OrderingApi';
import { DefaultNumberInputField } from '~/common/components/defaultUIComponents/DefaultNumberInputField';
import { DefaultModalDialog } from '~/common/components/defaultUIComponents/defaultModalDialog';
import { CheckboxSelect } from '~/common/components/defaultUIComponents/CheckboxSelect';
import { InputField } from '~/common/components/defaultUIComponents/InputField';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import useTranslate from '~/common/utils/translate';
import { OrderedProject } from '@its4plan/ordering';
import { Box, useToast } from '@chakra-ui/react';

interface EditValues {
  priority: number;
  amount: number;
  comment: string;
  optionIds: string[];
}

export function EditOrderedProjectModal(props: {
  orderedProject: OrderedProject | AggregatedOrderedProject;
  isOpen: boolean;
  close: () => void;
}) {
  const toast = useToast();
  const t = useTranslate();
  const [{ data: project }] = ProjectApi.hooks.useProjectQuery(
    {
      id: getProjectId(props.orderedProject),
    },
    { enabled: props.isOpen },
  );

  const { mutate: update } = useUpdateOrderedProject({
    onSuccess() {
      props.close();
    },
    onError() {
      toast({
        title: t('ordertile.modal.editorder.error'),
        isClosable: true,
      });
    },
  });

  return (
    <DefaultModalDialog
      dialogHeaderText="ordertile.modal.editOrder.dialogHeaderText"
      submitText="ordertile.modal.editOrder.submitText"
      isOpen={props.isOpen}
      onCancel={props.close}
      onSubmit={(values: EditValues) => {
        update({
          id: props.orderedProject.id,
          projectOrder: {
            ...values,
            projectId: getProjectId(props.orderedProject),
            options: values.optionIds.map(Number),
          },
        });
      }}
      showCancelButton
      size="xl"
      initialValues={
        {
          amount: props.orderedProject.amount,
          comment: props.orderedProject.comment ?? '',
          optionIds: props.orderedProject?.options.map((o) =>
            getProjectId(o).toString(),
          ),
          priority: props.orderedProject.priority,
        } satisfies EditValues
      }
    >
      <Box>
        <DefaultNumberInputField
          name="priority"
          label="ordertile.modal.editOrder.priority.label"
          min={1}
          mb={2}
        />
        <DefaultNumberInputField
          name="amount"
          label="ordertile.modal.editOrder.amount.label"
          min={1}
          mb={2}
        />
        <InputField
          name="comment"
          placeholder=""
          label="ordertile.modal.editOrder.comment.label"
          mb={2}
          textarea={true}
        />
        <CheckboxSelect
          label="createOrder.options.label"
          name="optionIds"
          fields={project?.options ?? []}
          keys={(sp) => sp.id}
          labels={(sp) => sp.name}
          values={(sp) => sp.id}
        />
      </Box>
    </DefaultModalDialog>
  );
}

function getProjectId(
  orderedProject: OrderedProject | AggregatedOrderedProject,
): number {
  return 'foreignId' in orderedProject.project
    ? orderedProject.project.foreignId
    : orderedProject.project.id;
}
